import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
  Button,
  ComposedModal,
  Dropdown,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
  Toggletip,
  ToggletipActions,
  ToggletipButton,
  ToggletipContent,
} from '@carbon/react';
import { Information } from '@carbon/icons-react';
import { DataTable } from 'components/data-table';
import type { DataTableSelectionState } from 'components/data-table/types';
import { toastAtom } from 'pages/type-curves/atoms';
import type { InventoryWorkspaceResponse } from 'types/api-responses';
import { createInventoryWorkspaceAtom } from './atoms';
import {
  CANDIDATES_DEFAULT_PAGE_SIZE,
  useInventoryFormationCandidates,
} from './use-inventory-formation-candidates';
import {
  buildInventoryContextColumns,
  packageOptionKey,
  rowHasOriginTypeCurve,
  type ContextPackageOption,
  type FormationRow,
} from './inventory-context-columns';
import { InventoryWorkspaceAreaPreview } from './inventory-workspace-area-preview';
import { AreaDataModelModal } from './area-data-model-modal';
import type {
  TreeZonePackage,
  UpdatedContextRow,
} from 'pages/inventory-workspace/tree-leaves';
import { useLaunchableTypeCurves } from './use-launchable-type-curves';
import { usePortaledListBoxMenu } from './use-portaled-list-box-menu';
import style from './create-inventory-workspace-modal.module.scss';

export interface CreateInventoryWorkspaceModalProps {
  open: boolean;
  /** The type curve the workspace launches from. Absent = opened from the
   *  listing, where the modal offers its own area picker instead. */
  typeCurveHeaderDetailsId: number | null | undefined;
  /** The launched type curve's `uniqueTcId` — the candidates query's only
   *  input, and `launchedUniqueTcId` on the create payload. */
  uniqueTcId?: string | null;
  /**
   * `eval` only: the run the page is showing, when its picks still match the run. LTD Properties
   * then counts what that run valued wherever a row is still on the curve and package it priced.
   */
  calculationId?: number | null;
  /** Scenario the workspace belongs to — `typeScenarioId` on the payload. */
  typeCurveScenarioId?: number | null;
  /** Area name shown for the target in the preview legend. */
  typeCurveAreaName?: string | null;
  onClose: () => void;
  onCreated?: (workspace: InventoryWorkspaceResponse) => void;
  /**
   * `create` opens the new-workspace form. `edit-context` is the same modal —
   * same table, columns, map and layout — reopened from inside a workspace to
   * add or drop Type Curves. `eval` is the Eval 3 TC Manager: the
   * same table, columns and map anchored on a PACKAGE (`packageId` +
   * `typeCurveScenarioId`), showing what the scenario resolves for the package's assets; Apply
   * hands the operator's changed picks back through `onApplyEval`.
   */
  mode?: 'create' | 'edit-context' | 'eval';
  /** `eval` only: the package whose assets anchor the candidate set. */
  packageId?: number | null;
  /** `edit-context` only: pairs to open ticked (the workspace's current zones);
   *  the rest of the candidate rows show unticked and can be added. */
  initialIncluded?: readonly string[];
  /**
   * `edit-context` only: what the workspace is using now, keyed by zone id —
   * the left nav's own selection. The dropdowns open on these rather than on
   * the candidates' defaults, because in edit mode the question is what this
   * workspace uses, not what a new one would get.
   */
  initialTypeCurves?: Record<string, string>;
  /** `edit-context` only: current package per zone, by `inventoryFormationPackageId`. */
  initialPackages?: Record<string, number>;
  /**
   * `edit-context` only: the workspace's OWN packages per zone id — the
   * inventory dropdown's source for zones the workspace already covers. These
   * carry `inventoryFormationPackageId`, so a pick can re-key the selected leaf;
   * the launch candidate list (used only for zones being ADDED) cannot.
   */
  workspacePackagesByZone?: Record<string, TreeZonePackage[]>;
  /**
   * `edit-context` submit. Frontend-only: the full resolved context of every
   * checked row (its chosen type curve + inventory package) is handed back — one
   * row per inclusion, so the caller can build a sidebar leaf even for a zone the
   * workspace wasn't created with. `includedIds` is the COMPLETE set of checked
   * zone ids, not just the rows resolved this session: the candidate table is
   * server-paginated, so a checked zone on a page the operator never re-surfaced
   * has no `UpdatedContextRow` but must still count as included. Nothing is
   * persisted.
   */
  onUpdateContext?: (rows: UpdatedContextRow[], includedIds: string[]) => void;
  /**
   * `eval` only: committed choices to open on, keyed by row id
   * (`"<area>:<formation>"`). Curves by header-detail id, packages by
   * (name, spacing) — the identities the eval's override log carries. A row
   * with no entry opens on the server default, exactly as before.
   */
  evalInitialTypeCurves?: Record<string, number>;
  evalInitialPackages?: Record<
    string,
    { name: string; wellSpacingFt: number | null }
  >;
  /**
   * `eval` only: Apply hands back the resolved choices of every row LOADED this
   * session (server pagination — an unvisited page contributes nothing, so its
   * committed choices stay untouched). Frontend-only, like `onUpdateContext`:
   * the caller folds these into its own override deltas; nothing is persisted
   * here. Absent, the eval footer stays Close-only.
   */
  onApplyEval?: (rows: EvalResolvedRow[]) => void;
  /**
   * `eval` only: extra map content, given the slot rows the table is pointing at — the selected
   * row's, else the union across the rows on screen. Rendered inside the preview's GL canvas, so
   * the caller hands back react-map-gl `Source`/`Layer` children (the eval draws its LTD sticks).
   *
   * A render prop rather than a node because the ids change with the selection and with the
   * Inventory dropdown, and only this component knows both. Absent — every non-eval caller — the
   * preview renders exactly as it did.
   */
  renderMapOverlay?: (slotIds: readonly number[]) => ReactNode;
}

/**
 * One eval row's resolved dropdown state at Apply — the chosen curve and
 * inventory package, each flagged when it sits on the server default so the
 * caller can emit a revert instead of an override.
 */
export interface EvalResolvedRow {
  typeCurveAreaId: number | null;
  typeCurveAreaName: string;
  formation: string;
  chosenTypeCurveHeaderDetailsId: number | null;
  isDefaultTypeCurve: boolean;
  chosenPackageName: string | null;
  chosenPackageSpacingFt: number | null;
  isDefaultPackage: boolean;
  // Which picks the operator changed. The caller commits only those, so a row whose
  // saved choice didn't seed (no longer in its list) isn't cleared just by being seen.
  typeCurveChanged: boolean;
  packageChanged: boolean;
}

const CREATE_PATH = '/inventory_workspaces';

/** One (area, formation) pair as the create payload carries it. */
interface CreatePair {
  typeCurveArea: string;
  formationName: string;
}

/**
 * Launch picker for a new Inventory Workspace.
 *
 * Every (area, formation) pair the area config maps arrives pre-selected; the
 * launched pair is locked on. Neither units nor type curves are chosen here —
 * the server loads every intersecting unit and resolves each pair's type curves
 * itself — so the submitted body carries the name, the launch context and the
 * chosen pairs.
 *
 * @param {CreateInventoryWorkspaceModalProps} props - Open state, launch context and callbacks.
 * @return {JSX.Element} The modal.
 */
export const CreateInventoryWorkspaceModal = ({
  open,
  typeCurveHeaderDetailsId,
  uniqueTcId,
  typeCurveScenarioId,
  typeCurveAreaName,
  onClose,
  onCreated,
  mode = 'create',
  packageId,
  calculationId = null,
  initialIncluded,
  initialTypeCurves,
  initialPackages,
  workspacePackagesByZone,
  onUpdateContext,
  evalInitialTypeCurves,
  evalInitialPackages,
  onApplyEval,
  renderMapOverlay,
}: CreateInventoryWorkspaceModalProps) => {
  const isEditContext = mode === 'edit-context';
  const isEval = mode === 'eval';
  const [{ loading, result }, createWorkspace] = useAtom(
    createInventoryWorkspaceAtom
  );
  const setToast = useSetAtom(toastAtom);

  const [name, setName] = useState('');
  // Only used when the modal is opened without a type curve — from the
  // listing, where nothing implies one. Launched from a type curve page the
  // prop is set and this stays null.
  const [pickedArea, setPickedArea] = useState<string | null>(null);
  // Composite `"<area>:<formation>"` ids — formation alone repeats across areas.
  const [included, setIncluded] = useState<Set<string>>(new Set());
  // Every pair seen so far, by id. The submitted body is built from this rather
  // than from the page in hand, which holds only the pairs currently on screen.
  const [pairsById, setPairsById] = useState<Map<string, CreatePair>>(
    new Map()
  );
  // The active ROW, shared with the preview map in both directions — the mock's
  // `activeRowId`. Its area is derived, not stored, so a row and its area can
  // never disagree.
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  // An area selected on the map that no row backs — see activeAreaName below.
  const [activeAreaOnly, setActiveAreaOnly] = useState<string | null>(null);
  const [hiddenAreaNames, setHiddenAreaNames] = useState<string[]>([]);
  // Per-row overrides for the two dropdowns, keyed by row id. Absent means the
  // row is still on the server's default, which is the common case — storing
  // only the overrides keeps a candidates refetch from clobbering a choice.
  const [pickedTypeCurve, setPickedTypeCurve] = useState<
    Record<string, string>
  >({});
  const [pickedPackage, setPickedPackage] = useState<Record<string, string>>(
    {}
  );

  // Eval mode is package-anchored, so nothing implies (or needs) a launch type curve.
  const needsTypeCurvePicker = !typeCurveHeaderDetailsId && !isEval;
  const { data: launchableTypeCurves } = useLaunchableTypeCurves({
    enabled: open && needsTypeCurvePicker,
  });

  // The areas a workspace can be launched from.
  const areaOptions = useMemo(
    () =>
      [
        ...new Set(
          (launchableTypeCurves ?? [])
            .map((tc) => tc.typeCurveArea)
            .filter(Boolean)
        ),
      ].sort(),
    [launchableTypeCurves]
  );

  // The candidates query and the create payload both key off the launched type
  // curve, not an area — the server resolves the scenario and area configs from
  // it. Opened from the listing there is no type curve in context, so the picked
  // area is resolved to one of its type curves. Which one is a guess the backend
  // should settle: no query takes an area directly.
  const launchTypeCurve = useMemo(() => {
    if (uniqueTcId && typeCurveScenarioId) {
      return { uniqueTcId, typeCurveScenarioId };
    }
    const match = (launchableTypeCurves ?? []).find(
      (tc) => tc.typeCurveArea === pickedArea
    );
    return match
      ? {
          uniqueTcId: match.uniqueTcId,
          typeCurveScenarioId: match.typeCurveScenarioId,
        }
      : null;
  }, [uniqueTcId, typeCurveScenarioId, launchableTypeCurves, pickedArea]);

  // The candidates query is anchored on the family alone — the scenario id is a
  // create-payload concern. edit-context has no scenario id to hand (the
  // workspace response carries none) and never creates, so gating the query on
  // `launchTypeCurve` would leave its table permanently empty.
  const candidatesUniqueTcId = isEditContext
    ? (uniqueTcId ?? undefined)
    : launchTypeCurve?.uniqueTcId;

  const {
    rows: allCandidates,
    totalCount: candidatesTotalCount,
    areas: candidateAreas,
    isLoading: candidatesLoading,
    error,
    loadData,
  } = useInventoryFormationCandidates(candidatesUniqueTcId, {
    // The hook itself waits for an anchor or a type curve.
    enabled: open,
    // The package anchor (eval mode): the server derives areas and units from the package,
    // and the LTD / unit counts come back scoped to the package's own assets.
    packageAnchor:
      isEval && packageId && typeCurveScenarioId
        ? { packageId, typeCurveScenarioId, calculationId }
        : null,
  });

  // Both modes see every candidate the backend returns for the launched family.
  // edit-context pre-checks the rows already in the workspace (initialIncluded)
  // and leaves the rest unchecked — checking one ADDS that zone to the sidebar.
  const results = allCandidates;

  const totalCount = candidatesTotalCount;

  // A Carbon Dropdown renders its menu inline, so inside a scrolling table in a
  // modal it is clipped by both. This relocates the open menu outside that
  // stacking context and pins it to its field — the same fix the Type Curve
  // workspace modal uses for its ComboBoxes.
  const menuPortalContainerRef = usePortaledListBoxMenu<HTMLDivElement>(
    open,
    style.menuPortalHost
  );

  // Clear the name each time the modal opens. This is a reset, not a seed: the
  // page keeps the modal mounted whenever the type curve has an id, so state
  // survives a close, and a name typed then cancelled would still be there on
  // the next open. Matches create-type-curve-workspace-modal.tsx.
  // Create mode selects every row by default; a row the operator clears is
  // recorded here so the seed effect below never re-adds it (on a later page or
  // a refetch). State, not a ref — the seed effect's updater must stay pure, so
  // the row it consults cannot be mutated from inside that updater.
  const [deselected, setDeselected] = useState<Set<string>>(new Set());
  // Eval mode: row ids already seeded from the committed choices this open. Seeding is
  // once per row — a candidates refetch must not clobber a fresh pick, including a pick
  // BACK to the default (which deletes nothing from the pick maps).
  const evalSeededIdsRef = useRef<Set<string>>(new Set());
  // Every eval row seen this session, in its latest DERIVED state (the same `_default*`
  // fields the cells render). The table is server-paginated, so Apply must answer for
  // rows on pages no longer in hand; a snapshot last moved while its row was on screen,
  // which is also the last moment its picks could change. A ref, not state: it never
  // drives a render — it is read once, inside the Apply click.
  const evalRowsRef = useRef<Map<string, FormationRow>>(new Map());
  // Eval mode: the picks the operator changed this session, per row. Apply returns only
  // these rows (see EvalResolvedRow). A curve pick re-derives the package, so it marks both.
  const evalChangedRef = useRef<
    Map<string, { typeCurve: boolean; package: boolean }>
  >(new Map());

  useEffect(() => {
    if (!open) return;
    setName('');
    setPickedArea(null);
    setDeselected(new Set());
    evalSeededIdsRef.current = new Set();
    evalRowsRef.current = new Map();
    evalChangedRef.current = new Map();
    // edit-context opens on what the workspace covers now, so a row the
    // operator never touches stays included when they submit.
    setIncluded(new Set(initialIncluded ?? []));
    setPairsById(new Map());
    // Seeded, not cleared: an unseeded row falls back to the candidates'
    // default, which in edit mode would silently show a different curve from
    // the one the workspace is actually using.
    setPickedTypeCurve({ ...(initialTypeCurves ?? {}) });
    // The dropdown tracks picks by `packageOptionKey`; the workspace's current
    // package per zone arrives as a raw id, so seed it in the same key space.
    setPickedPackage(
      Object.fromEntries(
        Object.entries(initialPackages ?? {}).map(([zoneId, packageId]) => [
          zoneId,
          `id:${packageId}`,
        ])
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Nothing is selected by default — the payload enumerates the chosen pairs,
  // and with the table server-paginated a pre-selection could only ever cover
  // the page in hand. The origin pair is the exception: it ships with the
  // workspace by definition, so it is added the moment a page surfaces it.
  useEffect(() => {
    if (!open || results.length === 0) return;
    setPairsById((prev) => {
      const next = new Map(prev);
      let changed = false;
      results.forEach((row) => {
        if (next.has(row.id)) return;
        next.set(row.id, {
          typeCurveArea: row.typeCurveArea,
          formationName: row.formation,
        });
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [open, results]);

  // Eval mode: the first time a row surfaces, open its dropdowns on the eval's COMMITTED
  // choices rather than the server defaults — the eval-mode mirror of edit-context's
  // initialTypeCurves/initialPackages, which key by identities the eval does not hold
  // (uniqueTcId / inventoryFormationPackageId). Committed identities are the override
  // log's: header-detail id for the curve, (name, spacing) for the package.
  useEffect(() => {
    if (!open || !isEval || results.length === 0) return;
    const fresh = results.filter(
      (row) => !evalSeededIdsRef.current.has(row.id)
    );
    if (fresh.length === 0) return;
    fresh.forEach((row) => evalSeededIdsRef.current.add(row.id));
    setPickedTypeCurve((prev) => {
      const next = { ...prev };
      fresh.forEach((row) => {
        const committedId = evalInitialTypeCurves?.[row.id];
        if (committedId === undefined || row.id in next) return;
        const match = row.typeCurves.find(
          (tc) => tc.typeCurveHeaderDetailsId === committedId
        );
        if (match) next[row.id] = match.uniqueTcId;
      });
      return next;
    });
    setPickedPackage((prev) => {
      const next = { ...prev };
      fresh.forEach((row) => {
        const committed = evalInitialPackages?.[row.id];
        if (!committed || row.id in next) return;
        const match = (row.inventoryPackages ?? []).find(
          (p) =>
            p.name === committed.name &&
            p.wellSpacingFt === committed.wellSpacingFt
        );
        if (!match) return;
        next[row.id] = packageOptionKey({
          inventoryFormationPackageId: null,
          name: match.name,
          wellSpacingFt: match.wellSpacingFt,
          displayName: match.displayName,
          isDefault: match.isDefault,
        });
      });
      return next;
    });
  }, [open, isEval, results, evalInitialTypeCurves, evalInitialPackages]);

  // Create mode: every pair is checked by default (matches the mock), except the
  // ones the operator has explicitly cleared. The updater is PURE — it only
  // reads `deselected` and `prev` — so React's StrictMode double-invoke of the
  // updater is idempotent. (The previous version mutated a ref inside the
  // updater; the discarded first invocation filled it, the second saw every row
  // already present and added nothing, leaving the whole table unchecked.)
  useEffect(() => {
    if (!open || isEditContext || isEval || results.length === 0) return;
    setIncluded((prev) => {
      let changed = false;
      const next = new Set(prev);
      results.forEach((row) => {
        if (deselected.has(row.id) || next.has(row.id)) return;
        next.add(row.id);
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [open, results, isEditContext, isEval, deselected]);

  // Represent the selection the way the (server-driven) DataTable's header
  // expects. Feeding `selectAll: false` always kept `included` in `toggledNodes`,
  // but the header's checked state is `selectAll && toggledNodes.size === 0` —
  // so it never reached "checked", the box sat indeterminate, and its click
  // could only ever fire select-all, never unselect-all. When every selectable
  // (non-locked) row on screen is included, hand back `selectAll: true` so the
  // header checks and its next click clears. Locked rows (the launched pair) are
  // always included and are not exceptions.
  const selection = useMemo<DataTableSelectionState>(() => {
    const selectable = results.filter((row) => !rowHasOriginTypeCurve(row));
    const allSelected =
      selectable.length > 0 && selectable.every((row) => included.has(row.id));
    return allSelected
      ? { selectAll: true, toggledNodes: [] }
      : { selectAll: false, toggledNodes: [...included] };
  }, [results, included]);

  // Only the rows on screen are reconciled. Rebuilding the whole set from the
  // current page would drop every pair chosen on a page since navigated away.
  const handleSelectionChange = (next: DataTableSelectionState) => {
    const reported = new Set(next.toggledNodes);
    const selectedIn = (rowId: string) => {
      return next.selectAll ? !reported.has(rowId) : reported.has(rowId);
    };
    setIncluded((prev) => {
      const resolved = new Set(prev);
      rows.forEach((row) => {
        // A locked row (the launched pair) never changes on a select/clear-all —
        // it ships with the workspace and its checkbox is disabled.
        if (!row._canSelect) return;
        if (selectedIn(row.id)) resolved.add(row.id);
        else resolved.delete(row.id);
      });
      return resolved;
    });
    // Create mode defaults to all-selected, so a clear has to be remembered or
    // the seed effect would re-add the row the moment the results identity
    // changes (a page turn or a refetch).
    if (!isEditContext) {
      setDeselected((prev) => {
        const resolved = new Set(prev);
        rows.forEach((row) => {
          if (!row._canSelect) return;
          if (selectedIn(row.id)) resolved.delete(row.id);
          else resolved.add(row.id);
        });
        return resolved;
      });
    }
  };

  // Clicking a row activates it; clicking the active row again clears it.
  const toggleActiveRow = useCallback((rowId: string) => {
    setActiveAreaOnly(null);
    setActiveRowId((prev) => (prev === rowId ? null : rowId));
  }, []);

  // The map areas, with inline geometry from the candidates response. Kept in the
  // response's order (target first, then descending overlap), which also fixes
  // the legend/chip colour slots. edit-context narrows `results` to the included
  // zones, so drop any area no shown row uses to keep the map and table in step.
  const previewAreas = useMemo(() => {
    const namesInPlay = new Set<string>();
    if (typeCurveAreaName) namesInPlay.add(typeCurveAreaName);
    results.forEach((row) => namesInPlay.add(row.typeCurveArea));
    return candidateAreas.filter((area) => namesInPlay.has(area.name));
  }, [candidateAreas, results, typeCurveAreaName]);

  // Everything the cells read travels on the row, so the column defs can stay
  // referentially stable — see FormationRow.
  const rows = useMemo<FormationRow[]>(
    () =>
      results.map((row) => {
        const defaultTypeCurve =
          row.typeCurves.find((tc) => tc.isDefault) ??
          row.typeCurves[0] ??
          null;
        // A dropdown choice wins over the default; falling back keeps a row the
        // operator never touched on whatever the server currently defaults to.
        const chosenTypeCurve =
          row.typeCurves.find(
            (tc) => tc.uniqueTcId === pickedTypeCurve[row.id]
          ) ?? defaultTypeCurve;
        // The inventory dropdown lists the workspace's OWN packages for a zone
        // it already covers — those carry the `inventoryFormationPackageId` a
        // pick needs to re-key the selected leaf. A zone being ADDED has none,
        // so it falls back to the launch candidates (idless; their pick can't
        // re-key, which is the existing frontend-only add behaviour). Create
        // mode always uses candidates. Packages belong to the (area, formation)
        // zone, not to a type curve; the type-curve fallback is a bridge for a
        // backend that still nests candidates there.
        const ownPackages = workspacePackagesByZone?.[row.id];
        const candidatePackages = row.inventoryPackages?.length
          ? row.inventoryPackages
          : (chosenTypeCurve?.inventoryPackages ?? []);
        const packages: ContextPackageOption[] =
          ownPackages && ownPackages.length > 0
            ? ownPackages.map((pkg) => ({
                inventoryFormationPackageId: pkg.inventoryFormationPackageId,
                name: pkg.name,
                wellSpacingFt: pkg.wellSpacingFt,
                displayName: pkg.displayName,
                isDefault: pkg.isDefault,
              }))
            : candidatePackages.map((pkg) => ({
                inventoryFormationPackageId: null,
                name: pkg.name,
                wellSpacingFt: pkg.wellSpacingFt,
                displayName: pkg.displayName,
                isDefault: pkg.isDefault,
                ltdProperties: pkg.ltdProperties,
                valuedLTDProperties: pkg.valuedLTDProperties,
                calculationValued: pkg.calculationValued,
                // Package-anchor extras (absent on a launch response): the rows behind that
                // count and their extent, which the eval's map draws and frames.
                ltdSlotIds: pkg.ltdSlotIds,
                bbox: pkg.bbox,
              }));
        // A zone carries the same package name at several spacings, and only the
        // one matching the curve is the same development plan. No match falls
        // back to the whole list, which is the behaviour before matching.
        const tcSpacingFt = chosenTypeCurve?.wellSpacingFt ?? null;
        const spacingMatches =
          tcSpacingFt === null
            ? []
            : packages.filter((p) => p.wellSpacingFt === tcSpacingFt);
        const defaultPool =
          spacingMatches.length > 0 ? spacingMatches : packages;
        const defaultPackage =
          defaultPool.find((p) => p.isDefault) ?? defaultPool[0] ?? null;

        const chosenPackage =
          packages.find((p) => packageOptionKey(p) === pickedPackage[row.id]) ??
          defaultPackage;

        return {
          ...row,
          // The launched pair ships with the workspace by definition — the row
          // whose TYPE CURVE is the origin, not every row in the origin area.
          _canSelect: !rowHasOriginTypeCurve(row),
          _defaultTypeCurve: chosenTypeCurve,
          _defaultPackage: chosenPackage,
          _packages: packages,
          // On its default pick (not operator-overridden) → shows the "Default"
          // chip. Keyed on the pick, not the server `isDefault` flag, which the
          // single-curve-per-zone responses leave false.
          _isDefaultTypeCurve:
            !!chosenTypeCurve && chosenTypeCurve === defaultTypeCurve,
          _isDefaultPackage:
            !!chosenPackage && chosenPackage === defaultPackage,
          // Same palette slot the preview map gives this area, so the chip in
          // the table and the polygon read as one thing.
          _areaColorIndex: Math.max(
            previewAreas.findIndex((a) => a.name === row.typeCurveArea),
            0
          ),
          _isActive: row.id === activeRowId,
        };
      }),
    [
      results,
      activeRowId,
      previewAreas,
      pickedTypeCurve,
      pickedPackage,
      workspacePackagesByZone,
    ]
  );

  // Keep the eval snapshot current for every row in hand (see evalRowsRef).
  useEffect(() => {
    if (!isEval) return;
    rows.forEach((row) => evalRowsRef.current.set(row.id, row));
  }, [isEval, rows]);

  // Apply (eval only): hand back the resolved choices of every row the operator changed.
  // The caller folds them into override deltas and owns closing — a failed commit must not
  // lose the operator's picks behind a closed modal.
  const handleApplyEval = useCallback(() => {
    if (!onApplyEval) return;
    const resolved: EvalResolvedRow[] = [];
    evalChangedRef.current.forEach((changed, rowId) => {
      const row = evalRowsRef.current.get(rowId);
      if (!row) return;
      resolved.push({
        typeCurveAreaId: row.typeCurveAreaId ?? null,
        typeCurveAreaName: row.typeCurveArea,
        formation: row.formation,
        chosenTypeCurveHeaderDetailsId:
          row._defaultTypeCurve?.typeCurveHeaderDetailsId ?? null,
        isDefaultTypeCurve: row._isDefaultTypeCurve,
        chosenPackageName: row._defaultPackage?.name ?? null,
        chosenPackageSpacingFt: row._defaultPackage?.wellSpacingFt ?? null,
        isDefaultPackage: row._isDefaultPackage,
        typeCurveChanged: changed.typeCurve,
        packageChanged: changed.package,
      });
    });
    onApplyEval(resolved);
  }, [onApplyEval]);

  const handlePickTypeCurve = useCallback(
    (rowId: string, uniqueTcId: string) => {
      evalChangedRef.current.set(rowId, { typeCurve: true, package: true });
      setPickedTypeCurve((prev) => ({ ...prev, [rowId]: uniqueTcId }));
      // The package pick was made against the OLD curve and may now sit at a
      // spacing the new one does not use, so drop it and let the row re-match.
      setPickedPackage((prev) => {
        if (!(rowId in prev)) return prev;
        const next = { ...prev };
        delete next[rowId];
        return next;
      });
    },
    []
  );

  const handlePickPackage = useCallback((rowId: string, packageKey: string) => {
    const changed = evalChangedRef.current.get(rowId);
    evalChangedRef.current.set(rowId, {
      typeCurve: changed?.typeCurve ?? false,
      package: true,
    });
    setPickedPackage((prev) => ({ ...prev, [rowId]: packageKey }));
  }, []);

  const columns = useMemo(
    () =>
      buildInventoryContextColumns({
        onActivate: toggleActiveRow,
        // Eval only: there the area name opens the row's curve in a new tab, and the Formation /
        // LTD Properties cells carry the row activation.
        linkAreaToTypeCurve: isEval,
        showValuedLtd: isEval,
        onPickTypeCurve: handlePickTypeCurve,
        onPickPackage: handlePickPackage,
      }),
    [isEval, toggleActiveRow, handlePickTypeCurve, handlePickPackage]
  );

  // Every area in play — including neighbours no row currently uses — so the
  // map shows the surrounding context, as the mock does. Target first, then
  // Only the active row highlights — the map's yellow fill is the "Selected row"
  // key in the legend, so exactly one row and one area are ever emphasised.
  const highlightedRowIds = useMemo(
    () => (activeRowId ? new Set([activeRowId]) : undefined),
    [activeRowId]
  );

  // The active row's area drives the map. `activeAreaOnly` covers areas with no
  // row behind them — the launched area before candidates load, or a neighbour
  // whose formations are all deselected. Without it, clicking such an area on
  // the map would do nothing at all.
  const activeRow = useMemo(
    () => rows.find((row) => row.id === activeRowId) ?? null,
    [rows, activeRowId]
  );

  const activeAreaName = activeRow?.typeCurveArea ?? activeAreaOnly;

  // ── What the eval's map draws and frames ────────────────────────────────────────────────
  //
  // Both read the CHOSEN package (`_defaultPackage` already reflects a pick), so switching the
  // Inventory dropdown redraws the sticks and reframes the camera with no request: the option
  // carries its own ids and extent, from the same aggregate as the count in its row.
  //
  // With no row selected the map shows the union across the rows on screen — the table's whole
  // answer at a glance — and the camera stays on the areas.

  const overlaySlotIds = useMemo<number[]>(() => {
    if (!isEval) return [];
    if (activeRow) return activeRow._defaultPackage?.ltdSlotIds ?? [];
    return rows.flatMap((row) => row._defaultPackage?.ltdSlotIds ?? []);
  }, [isEval, rows, activeRow]);

  // Precedence mirrors the sticks: the chosen package's extent, else the area's own box (a zone
  // with no places still frames somewhere sensible), else leave the camera alone.
  const activeSlotBounds = useMemo<
    [number, number, number, number] | null
  >(() => {
    if (!isEval || !activeRow) return null;
    return (
      activeRow._defaultPackage?.bbox ??
      previewAreas.find((area) => area.name === activeRow.typeCurveArea)
        ?.bbox ??
      null
    );
  }, [isEval, activeRow, previewAreas]);

  // Per-area hover-tooltip text for the preview map, keyed by area name. The
  // modal owns the context the label needs: the area's overlap % / Origin, and
  // whether it is "in context" (a shown row uses it) and "selected" (the active
  // area). e.g. `proj96_A — 41% overlap · in context · selected`.
  const areaTooltips = useMemo(() => {
    const map = new Map<string, string>();
    previewAreas.forEach((area) => {
      const overlapText = area.isOrigin
        ? 'Origin'
        : area.overlapPercent !== null && area.overlapPercent !== undefined
          ? `${area.overlapPercent}% overlap`
          : undefined;
      const inContext = results.some((r) => r.typeCurveArea === area.name);
      const selected = area.name === activeAreaName;
      const segments = [
        overlapText,
        inContext ? 'in context' : undefined,
        selected ? 'selected' : undefined,
      ].filter(Boolean);
      map.set(
        area.name,
        segments.length ? `${area.name} — ${segments.join(' · ')}` : area.name
      );
    });
    return map;
  }, [previewAreas, results, activeAreaName]);

  // Map → table: activate the row whose chosen type curve sits in that area; if
  // no row does, still select the area so the map responds to the click.
  const handleAreaClick = useCallback(
    (areaName: string) => {
      const match = results.find((row) => row.typeCurveArea === areaName);

      if (match) {
        setActiveAreaOnly(null);
        toggleActiveRow(match.id);
        return;
      }

      setActiveRowId(null);
      setActiveAreaOnly((prev) => (prev === areaName ? null : areaName));
    },
    [results, toggleActiveRow]
  );

  const nameTrimmed = name.trim();
  const canSubmit = isEditContext
    ? included.size > 0 && !candidatesLoading && !error
    : nameTrimmed.length > 0 &&
      included.size > 0 &&
      Boolean(launchTypeCurve) &&
      !candidatesLoading &&
      !error;

  const handleClose = () => {
    // Ignore dismissal while the request is in flight, as the Type Curve
    // workspace modal does.
    if (loading) return;
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    // Frontend-only: hand the selection back and let the caller apply it. No
    // endpoint edits a workspace's context, so nothing is persisted.
    if (isEditContext) {
      // Report the FULL resolved context for every included row — not just the
      // ones the operator changed. The page rebuilds the sidebar tree from this,
      // so a row it omits would silently drop from the workspace. `rows` already
      // resolves each row's chosen type curve and package (override → default).
      const contextRows: UpdatedContextRow[] = rows
        .filter((row) => included.has(row.id))
        .map((row) => {
          const tc = row._defaultTypeCurve;
          const pkg = row._defaultPackage;
          return {
            rowId: row.id,
            typeCurveArea: row.typeCurveArea,
            formation: row.formation,
            typeCurve: {
              uniqueTcId: tc?.uniqueTcId ?? '',
              label: tc?.label ?? '',
              typeCurveHeaderDetailsId: tc?.typeCurveHeaderDetailsId ?? null,
              wellSpacingFt: tc?.wellSpacingFt ?? null,
            },
            package: pkg
              ? {
                  inventoryFormationPackageId:
                    pkg.inventoryFormationPackageId ?? null,
                  name: pkg.name ?? '',
                  displayName: pkg.displayName ?? '',
                  wellSpacingFt: pkg.wellSpacingFt ?? null,
                }
              : null,
          };
        });
      onUpdateContext?.(contextRows, [...included]);
      onClose();
      return;
    }

    if (!launchTypeCurve) return;

    createWorkspace({
      url: CREATE_PATH,
      type: 'POST',
      data: {
        inventoryWorkspace: {
          name: nameTrimmed,
          typeScenarioId: launchTypeCurve.typeCurveScenarioId,
          launchedUniqueTcId: launchTypeCurve.uniqueTcId,
          typeCurves: [...pairsById.entries()].flatMap(([id, pair]) => {
            return included.has(id) ? [pair] : [];
          }),
        },
      },
      onSuccess: (workspace: InventoryWorkspaceResponse) => {
        const count = workspace.typeCurveTree?.typeCurveCount ?? 0;
        setToast({
          kind: 'success',
          title: 'Inventory Workspace created',
          subtitle: `${workspace.name} — ${count} type curve${
            count === 1 ? '' : 's'
          } attached.`,
        });
        // The new workspace opens in its own tab and the page behind stays
        // put, matching the Type Curve workspace modal. onCreated still runs so
        // the caller can refresh whatever list it is showing.
        window.open(workspace.href, '_blank');
        onCreated?.(workspace);
        onClose();
      },
    });
  };

  const createErrorSubtitle =
    result?.type === 'error'
      ? 'The workspace could not be created. Check the details and try again.'
      : null;

  // The info Toggletip's "View the Area data model" action opens a second modal
  // (rendered as a sibling below; Carbon portals each to body so stacking works).
  const [areaModelOpen, setAreaModelOpen] = useState(false);

  return (
    <>
      <ComposedModal
        open={open}
        onClose={handleClose}
        preventCloseOnClickOutside
        size="lg"
        className={style.modal}
      >
        <ModalHeader
          label={
            isEval
              ? 'Type Curve Scenario'
              : isEditContext
                ? 'Inventory Workspace'
                : 'Type Curve'
          }
          title={
            isEval ? (
              'Manage Type Curves'
            ) : isEditContext ? (
              <span className={style.titleWithInfo}>
                Add or Remove Type Curves
                {/* Carbon Toggletip — the mock's dark explainer with the caret and
                  the focus-boxed (i) trigger, click-to-open. */}
                <Toggletip align="bottom-left">
                  <ToggletipButton label="What is an inventory context?">
                    <Information />
                  </ToggletipButton>
                  <ToggletipContent>
                    <p className={style.toggletipHeading}>
                      What is an inventory context?
                    </p>
                    <p>
                      Inventory is multi-formation: the same units carry stacked
                      laterals, and units on a boundary are co-developed with
                      neighbouring Type Curve Areas. The context is the set of
                      (area, formation) pairs this workspace covers — each with
                      the Type Curve and Inventory realization it is measured
                      against.
                    </p>
                    <ToggletipActions>
                      <Button
                        kind="primary"
                        size="sm"
                        onClick={() => setAreaModelOpen(true)}
                      >
                        View the Area data model
                      </Button>
                    </ToggletipActions>
                  </ToggletipContent>
                </Toggletip>
              </span>
            ) : (
              'Create Inventory Workspace'
            )
          }
        />
        <ModalBody
          aria-label={
            isEval
              ? 'Manage Type Curves'
              : isEditContext
                ? 'Add or Remove Type Curves'
                : 'Create Inventory Workspace'
          }
        >
          <div className={style.body} ref={menuPortalContainerRef}>
            <p className={style.intro}>
              {isEval ? (
                <>
                  What the scenario resolves for this package&apos;s assets:
                  every Type Curve Area the package intersects, each
                  formation&apos;s default Type Curve and Inventory package, and
                  the LTD properties counted over the package&apos;s own units.
                </>
              ) : (
                <>
                  Inventory is multi-formation: the same units carry stacked
                  laterals. Pick which Type Curve Area and Formation pairs this
                  workspace covers. The Type Curve for each pair and the units
                  are both resolved automatically — every unit intersecting this
                  Type Curve&apos;s area.
                </>
              )}
            </p>

            {error && (
              <InlineNotification
                kind="error"
                title="Could not load formations"
                subtitle={error.message}
                lowContrast
                hideCloseButton
                className={style.notification}
              />
            )}
            {createErrorSubtitle && (
              <InlineNotification
                kind="error"
                title="Error!"
                subtitle={createErrorSubtitle}
                lowContrast
                hideCloseButton
                className={style.notification}
              />
            )}
            {result?.type === 'fatal' && (
              <InlineNotification
                kind="error"
                title="Error!"
                subtitle={result.message}
                lowContrast
                hideCloseButton
                className={style.notification}
              />
            )}

            {/* Target Type Curve Area and name sit on one line, as the mock's
              metaRow does. The picker is only present when the modal was
              opened without a type curve — from the listing. */}
            <div className={style.metaRow}>
              {needsTypeCurvePicker && (
                <div className={style.metaField}>
                  <Dropdown
                    id="create-inventory-workspace-area"
                    titleText="Target Type Curve Area"
                    label="Select an area"
                    disabled={loading}
                    items={areaOptions}
                    selectedItem={pickedArea}
                    itemToString={(item) => item ?? ''}
                    onChange={({ selectedItem }) =>
                      setPickedArea(selectedItem ?? null)
                    }
                  />
                </div>
              )}
              {mode === 'create' && (
                <div className={style.metaField}>
                  <TextInput
                    id="create-inventory-workspace-name"
                    labelText="Inventory Workspace name"
                    placeholder="Enter workspace name"
                    value={name}
                    disabled={loading}
                    autoFocus
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              )}
            </div>

            <InventoryWorkspaceAreaPreview
              areas={previewAreas}
              targetName={typeCurveAreaName}
              activeAreaName={activeAreaName}
              areaTooltips={areaTooltips}
              onAreaClick={handleAreaClick}
              hiddenAreaNames={hiddenAreaNames}
              onToggleAreaVisibility={(areaName) =>
                setHiddenAreaNames((prev) => {
                  if (prev.includes(areaName)) {
                    return prev.filter((n) => n !== areaName);
                  }
                  return [...prev, areaName];
                })
              }
              fitBounds={activeSlotBounds}
            >
              {renderMapOverlay?.(overlaySlotIds)}
            </InventoryWorkspaceAreaPreview>

            <div className={style.tableHeading}>
              <span>Inventory context</span>
              <span className={style.summary}>
                {/* Eval mode has no pair selection (nothing is created from the rows),
                    so the count would always read "0 of N" — noise, not signal. */}
                {isEval
                  ? `${totalCount} pairs`
                  : `${included.size} of ${totalCount} pairs selected`}
                {previewAreas.length > 0 && ` · ${previewAreas.length} areas`}
              </span>
            </div>

            <DataTable<FormationRow>
              data={rows}
              columns={columns}
              getRowId={(row) => row.id}
              // Seeded to match the query hook's own default, so the footer's
              // page count and the fetched page never disagree on mount.
              initialTableState={{
                page: 1,
                pageSize: CANDIDATES_DEFAULT_PAGE_SIZE,
              }}
              isLoading={candidatesLoading}
              error={error ?? null}
              controlledState={isEval ? undefined : { selection }}
              highlightedRowIds={highlightedRowIds}
              onSelectionChange={
                isEval ? undefined : (s) => handleSelectionChange(s.selection)
              }
              scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
              onPaginationChange={loadData}
              onGlobalSearchChange={loadData}
              searchPlaceholder="Search type curves…"
              config={{
                // Eval mode reads the rows (dropdown picks only) — nothing is created
                // from a pair selection, so the checkbox column is dropped entirely.
                ...(isEval ? {} : { selectionType: 'multi' as const }),
                // Server-paginated with server-side search (the candidates endpoint
                // takes a `text` param, like the Units / Wells tables). Sorting and
                // per-column filters stay off — the endpoint takes no sort/filter
                // params — and so do the controls that need every row in hand.
                sorting: false,
                globalSearch: true,
                columnFilters: false,
                columnVisibility: true,
                // Not in the mock's toolbar.
                columnReordering: false,
                columnResizing: false,
                columnPinning: false,
                export: false,
              }}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          {isEval ? (
            // Eval mode never creates or updates a workspace: Apply hands the picks back to the
            // eval, which owns committing them as overrides and closing.
            <>
              <Button kind="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button
                kind="primary"
                onClick={handleApplyEval}
                disabled={candidatesLoading}
              >
                Apply
              </Button>
            </>
          ) : (
            <>
              <Button kind="secondary" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                kind="primary"
                onClick={handleSubmit}
                disabled={!canSubmit || loading}
              >
                {isEditContext
                  ? 'Update Type Curves'
                  : loading
                    ? 'Creating…'
                    : 'Create Inventory Workspace'}
              </Button>
            </>
          )}
        </ModalFooter>
      </ComposedModal>
      <AreaDataModelModal
        open={areaModelOpen}
        onClose={() => setAreaModelOpen(false)}
      />
    </>
  );
};
